import { useEffect, useState, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CatalogSchema } from '../types/navigation';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('useCatalogSchema');

export function useCatalogSchema(): CatalogSchema | undefined {
  const catalogApi = useApi(catalogApiRef);
  const [schema, setSchema] = useState<CatalogSchema>();
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef<AbortController>();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const loadSchema = async () => {
      logger.log('Loading catalog schema using facets API...');
      setLoading(true);
      abortControllerRef.current = new AbortController();

      try {
        // Simple approach: just fetch entities without caching complexity
        logger.time('catalog-fetch');
        const { items } = await catalogApi.getEntities({
          filter: [],
          fields: ['kind', 'spec.type', 'metadata.tags'],
        });
        logger.timeEnd('catalog-fetch');
        logger.log(`Fetched ${items.length} entities`);

        // Process entities to build schema (same as original logic)
        const kinds = new Set<string>();
        const typesByKind = new Map<string, Set<string>>();
        const tags = new Set<string>();

        items.forEach(entity => {
          kinds.add(entity.kind);

          if (entity.spec?.type) {
            const typeValue = String(entity.spec.type);
            if (!typesByKind.has(entity.kind)) {
              typesByKind.set(entity.kind, new Set());
            }
            typesByKind.get(entity.kind)!.add(typeValue);
          }

          if (entity.metadata?.tags) {
            entity.metadata.tags.forEach((tag: string) => tags.add(tag));
          }
        });

        const newSchema: CatalogSchema = {
          kinds: Array.from(kinds).sort(),
          typesByKind,
          tags: Array.from(tags).sort(),
        };

        logger.log('Catalog schema loaded:', {
          kinds: newSchema.kinds.length,
          types: Array.from(typesByKind.values()).reduce(
            (acc, set) => acc + set.size,
            0,
          ),
          tags: newSchema.tags.length,
        });

        if (isMountedRef.current) {
          setSchema(newSchema);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.log('Catalog schema load aborted');
        } else {
          logger.error('Failed to load catalog schema:', error);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadSchema();
  }, [catalogApi]);

  return loading ? undefined : schema;
}
